import {
    useContext,
    createContext,
    PropsWithChildren,
    useState,
    useEffect
} from "react";

import { getCurrentRoute } from "@/utils/get-current-route";

import { AGGClient } from "../../../clients";
import { getTemplateData } from "../../../utils/content-queries/get-content-data";
import { AgileTutorial } from "../tutorial-types";

export const AgileTutorialContext = createContext<AgileTutorial | null>(null);

const useAgileTutorialContextValue = (tutorial: AgileTutorial | null) => {
    const [contextValue, setContextValue] = useState(tutorial);

    useEffect(() => {
        const fetchAgileTutorialFromClient = async () => {
            const client = new AGGClient({
                gatewayUrl:
                    process.env["NODE_ENV"] === "production"
                        ? "https://www.atlassian.com/gateway/api/graphql"
                        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
            });

            const { route, locale } = getCurrentRoute();

            const agileTutorial = await getTemplateData(route, locale, client);
            setContextValue(agileTutorial);
        };

        if (!contextValue) {
            fetchAgileTutorialFromClient();
        }
        // empty dependency array so this effect only runs once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return contextValue;
};

export const AgileTutorialProvider = (
    props: PropsWithChildren<{ tutorial: AgileTutorial | null }>
) => {
    const value = useAgileTutorialContextValue(props.tutorial);

    return (
        <AgileTutorialContext.Provider value={value}>
            {props.children}
        </AgileTutorialContext.Provider>
    );
};

export const useAgileTutorial = (): AgileTutorial | null => {
    const tutorial = useContext(AgileTutorialContext);
    if (!tutorial) {
        console.warn(
            "SSR failed to fetch content on the server. Fetching content on the client."
        );
    }
    return tutorial;
};
